import React, { useState, useEffect, useRef } from 'react';
import CustomRefinementListContainer from './CustomRefinementListContainer';
import SortByContainer from './SortByContainer';
import CustomHitsPerPage from './CustomHitsPerPage';
import useSearchRedirectURL from '../../../components/search/useSearchRedirectURL';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { useInstantSearch } from 'react-instantsearch';
import { setSearchRefinements } from '../../../utils/algolia/search-insights';

// PNP-1822: Object filtering function
//@ts-ignore
Object.filter = (obj, predicate) =>
  Object.keys(obj)
    .filter(key => predicate(obj[key]))
    .reduce((res, key) => ((res[key] = obj[key]), res), {});

interface RefinementContainerProps {
  facets: Array<{ facet: string; type: string }>;
  collection?: any;
  indexName: string;
  priceRange: any;
  isSearchPage?: boolean;
  isDesktop?: boolean;
  shouldHandleRedirects?: boolean;
  nbSaleItems: number;
  nbClearanceItems: number;
  nbRedHotDealItems: number;
}

export const RefinementContainer = ({
  facets,
  collection,
  indexName,
  priceRange,
  isSearchPage = false,
  isDesktop = true,
  shouldHandleRedirects = false,
  nbSaleItems,
  nbClearanceItems,
  nbRedHotDealItems,
}: RefinementContainerProps) => {
  const { results, uiState } = useInstantSearch();
  const [mobileShowFilter, setMobileShowFilter] = useState(false);
  const [mobileShowSort, setMobileShowSort] = useState(false);
  const stickyEl = useRef<any>(null);
  const hasRedirect = useSearchRedirectURL(results);
  const [isSticky, setIsSticky] = useState(false);

  const searchState = uiState[indexName];

  useEffect(() => {
    setSearchRefinements(searchState.refinementList, searchState.range);
  }, [searchState.refinementList, searchState.range]);

  // PNP-1822: Highlighting refinement mobile button when a checkbox is checked
  const refinedFilters = {
    //@ts-ignore
    ...(searchState?.refinementList ? Object.filter(searchState?.refinementList, i => i.length > 0) : {}),
    ...(searchState?.range
      ? //@ts-ignore
        Object.filter(searchState?.range, i => {
          const rangeValues = i.split(':');
          return rangeValues[0] || rangeValues[1];
        })
      : {}),
  };

  const fixRefinement = () => {
    const refinementContainer = document.querySelector('.refinement-container') as any;
    const refinementSlider = document.querySelector('#facets-container > div.slick-slider');
    const pageHeader = document.querySelector('#shopify-section-header') as HTMLElement;
    const seoTextB = document.querySelector('.seotext-b');
    const seoTextBPos = seoTextB ? seoTextB.getBoundingClientRect().top : '';
    const scrollTop = refinementContainer?.nextSibling?.getBoundingClientRect().top - stickyEl.current?.offsetHeight;
    const searchRefinementContainer =
      isSearchPage && isDesktop ? (document.querySelector('.search-refinement-container div') as HTMLElement) : null;
    const searchResultsContainer = isSearchPage
      ? (document.querySelector('[data-search-results-content]') as HTMLElement)
      : null;
    const headerHeight = pageHeader?.offsetHeight || 0;

    if (
      scrollTop < stickyEl.current?.offsetHeight &&
      ((stickyEl && seoTextBPos > stickyEl.current?.offsetHeight) || !seoTextBPos)
    ) {
      if (!isSearchPage || !isDesktop) {
        refinementContainer.classList.add('scrollFix');
        refinementContainer.style.top = `${pageHeader?.offsetHeight || 0}px`;
        refinementSlider?.classList.add('scrollFix');
      }
      if (searchRefinementContainer) {
        searchRefinementContainer.classList.add('refinementScrollFix');
        const stopAtEl = document?.querySelector('.search-results-loop');
        let clampedTop = headerHeight;
        const searchRefinementHeight = searchRefinementContainer.getBoundingClientRect().height;
        if (stopAtEl) {
          const maxScrollPoint = stopAtEl.getBoundingClientRect().bottom;
          clampedTop =
            searchRefinementHeight + headerHeight > maxScrollPoint
              ? maxScrollPoint - searchRefinementHeight
              : headerHeight;
        }
        searchRefinementContainer.style.top = `${clampedTop}px`;
        if (searchResultsContainer) {
          searchResultsContainer.style.marginLeft = `${searchRefinementContainer.offsetWidth + 16}px`;
        }
      }
      setIsSticky(true);
    } else {
      refinementContainer.classList.remove('scrollFix');
      refinementContainer['style'].top = 0;
      refinementSlider?.classList.remove('scrollFix');
      if (searchRefinementContainer) {
        searchRefinementContainer.classList.remove('refinementScrollFix');
        if (searchResultsContainer) {
          searchResultsContainer.style.marginLeft = '0';
        }
      }
      setIsSticky(false);
    }
  };

  // Observer for shopify header randomly dropping down after checkbox selection.
  const headerObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      const mutationElement = mutation.target as HTMLElement;
      if (mutationElement.classList.contains('headroom--pinned')) {
        fixRefinement();
      }
    });
  });

  const mobileResize = () => {
    const width = window.innerWidth;
    if (width > 1010) {
      setMobileShowFilter(false);
    }
  };

  useEffect(() => {
    fixRefinement();
    const shopifyHeader = document.querySelector('#shopify-section-header');
    if (shopifyHeader) {
      headerObserver.observe(shopifyHeader, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    window.addEventListener('JB-header-nav-pin-change', fixRefinement);
    window.addEventListener('scroll', fixRefinement);
    window.addEventListener('resize', mobileResize);
    return () => {
      window.removeEventListener('JB-header-nav-pin-change', fixRefinement);
      window.removeEventListener('scroll', fixRefinement);
      window.removeEventListener('resize', mobileResize);
    };
  }, []);

  if (shouldHandleRedirects && hasRedirect) {
    return null;
  }

  const handleFilterClick = () => {
    setMobileShowFilter(true);
    pushToDataLayer({
      event: 'open_filters',
      search_type: 'site search',
    });
  };
  return (
    <>
      <div className="refinement-container">
        <div ref={stickyEl} className="sticky-filters">
          <button
            className={`refinement-mobile-filter ${Object.keys(refinedFilters).length > 0 ? 'highlighted' : ''}`}
            onClick={() => handleFilterClick()}
          >
            Filter {Object.keys(refinedFilters).length > 0 ? `(${Object.keys(refinedFilters).length})` : ''}
          </button>
          {!isSearchPage || !isDesktop ? (
            <CustomRefinementListContainer
              initialFacets={facets}
              nbHits={results?.nbHits}
              priceRange={priceRange}
              mobileShow={mobileShowFilter}
              setMobileShowFilter={setMobileShowFilter.bind(this)}
              trackingInfo={{ defaults: { hitsPerPage: 36, sortBy: 'relevancy' } }}
              isSearchPage={isSearchPage}
              isSticky={isSticky}
              nbSaleItems={nbSaleItems}
              nbClearanceItems={nbClearanceItems}
              nbRedHotDealItems={nbRedHotDealItems}
            />
          ) : null}
          <CustomHitsPerPage searchTerm={searchState.query} />
          <SortByContainer
            collection={collection}
            indexName={indexName}
            mobileShow={mobileShowSort}
            setMobileShowSort={setMobileShowSort}
            searchTerm={searchState.query}
          />
        </div>
      </div>
      <div
        className="refinement-filler"
        style={{ height: stickyEl.current ? `${stickyEl.current.offsetHeight}px` : 'initial' }}
      ></div>
    </>
  );
};
